/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { User } from '../domain/entities';
import { Zap, Shield, Eye, EyeOff, AlertCircle, Check } from 'lucide-react';

export function ForceChangePasswordPage({
  user,
  onPasswordChanged,
}: {
  user: User;
  onPasswordChanged: (newPassword: string) => Promise<void>;
}) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const minLength = newPassword.length >= 8;
  const hasNumber = /\d/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const isValid = minLength && hasNumber && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!minLength) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!hasNumber) {
      setError('Password must contain at least 1 number.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await onPasswordChanged(newPassword);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md space-y-6">

        {/* Logo */}
        <div className="flex items-center gap-3 justify-center">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
            <Zap size={20} className="text-white" />
          </div>
          <div>
            <div className="text-zinc-900 font-black text-base tracking-tight">ClickIt</div>
            <div className="text-indigo-600 text-[10px] font-bold uppercase tracking-widest">Platform</div>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-zinc-200 overflow-hidden">
          {/* Header */}
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Shield size={20} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-black text-zinc-900">Password Update Required</h2>
                <p className="text-xs text-amber-700 mt-0.5">
                  Welcome, <strong>{user.name}</strong>. You must set a new password to continue.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* New Password */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setError(''); }}
                  placeholder="Minimum 8 characters"
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-11 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  tabIndex={-1}
                >
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-11 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Validation checklist */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 space-y-1.5">
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Requirements</p>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${minLength ? 'bg-emerald-500' : 'bg-zinc-200'}`}>
                  {minLength && <Check size={10} className="text-white" />}
                </div>
                <span className={`text-xs ${minLength ? 'text-emerald-700 font-semibold' : 'text-zinc-400'}`}>At least 8 characters</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${hasNumber ? 'bg-emerald-500' : 'bg-zinc-200'}`}>
                  {hasNumber && <Check size={10} className="text-white" />}
                </div>
                <span className={`text-xs ${hasNumber ? 'text-emerald-700 font-semibold' : 'text-zinc-400'}`}>Contains at least 1 number</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordsMatch ? 'bg-emerald-500' : 'bg-zinc-200'}`}>
                  {passwordsMatch && <Check size={10} className="text-white" />}
                </div>
                <span className={`text-xs ${passwordsMatch ? 'text-emerald-700 font-semibold' : 'text-zinc-400'}`}>Passwords match</span>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                <AlertCircle size={14} className="text-red-500 shrink-0" />
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !isValid}
              className="w-full flex items-center justify-center gap-2 py-3 bg-zinc-900 text-white rounded-xl font-black text-sm hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Shield size={15} /> Update Password & Continue
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-[10px] text-zinc-300">
          This is a one-time security requirement set by your administrator.
        </p>
      </div>
    </div>
  );
}
